import React from 'react';
import type { UserRole } from '@/types';

// ---------------------------------------------------------------------------
// Permission constants — must match backend
// ---------------------------------------------------------------------------

export const PERMISSIONS = {
  // Dashboard
  DASHBOARD_VIEW: 'dashboard:view',
  DASHBOARD_ANALYTICS: 'dashboard:analytics',

  // Spaces
  SPACES_VIEW: 'space:view',
  SPACES_MANAGE: 'spaces:manage',
  SPACES_BOOK_DESK: 'spaces:book_desk',
  SPACES_BOOK_ROOM: 'spaces:book_room',
  SPACES_MANAGE_BOOKINGS: 'spaces:manage_bookings',
  /** Every booking with its title. Without it the caller sees their own
   *  bookings, meetings they are invited to, and free/busy (Employee rebuild A7). */
  BOOKING_VIEW_ALL: 'booking:view_all',
  /** The booking pages' first read (/book, /my-bookings). The FM Manager holds no
   *  booking:* at all (FM Manager Q8). */
  BOOKING_VIEW: 'booking:view',
  /** Move meeting catering orders on and read the pantry list — not the menu.
   *  Granted to reception (Employee follow-up). */
  CATERING_ORDER_STATUS: 'catering:order_status',

  // Helpdesk
  HELPDESK_VIEW: 'helpdesk:view',
  /** Every ticket, plus export, stats, the SLA board and analytics. Without
   *  it helpdesk:view reads only the caller's own tickets (Employee rebuild A1). */
  HELPDESK_VIEW_ALL: 'helpdesk:view_all',
  HELPDESK_CREATE: 'helpdesk:create',
  HELPDESK_MANAGE: 'helpdesk:manage',
  HELPDESK_ASSIGN: 'helpdesk:assign',
  HELPDESK_EDIT: 'helpdesk:edit',
  HELPDESK_CLOSE: 'helpdesk:close',
  HELPDESK_SLA_CONFIG: 'helpdesk:sla_config',
  HELPDESK_KB_MANAGE: 'helpdesk:kb_manage',
  /** FM Technician rebuild A4: read my assigned / Take-job / raised tickets and
   *  act on the ones assigned to me. Replaces helpdesk:edit on the technician,
   *  because helpdesk:edit expands to helpdesk:view_all. */
  HELPDESK_WORK: 'helpdesk:work',

  // Maintenance
  MAINTENANCE_VIEW: 'maintenance:view',
  MAINTENANCE_CREATE_WO: 'maintenance:create_wo',
  MAINTENANCE_MANAGE_WO: 'maintenance:manage_wo',
  MAINTENANCE_APPROVE_WO: 'maintenance:approve_wo',
  MAINTENANCE_PPM_MANAGE: 'maintenance:ppm_manage',
  MAINTENANCE_PARTS_MANAGE: 'maintenance:parts_manage',
  // The strings the BACKEND actually enforces, from the split of
  // maintenance:edit. WORK is doing an assigned job, INVENTORY is moving
  // stock, CONFIGURE is changing the rules; BMS_CONTROL commands plant and is
  // not a maintenance permission at all.
  //
  // NOTE for whoever reads this next: several names above — create_wo,
  // manage_wo, approve_wo, ppm_manage, parts_manage — are strings the backend
  // has never heard of. hasPermFor prefers the effective list from /auth/me,
  // so they are inert rather than wrong in practice, but this map is not a
  // description of what the server enforces. Reconciling it in full is its own
  // change; these four are added because BUG_04 turned on exactly that gap.
  MAINTENANCE_WORK: 'maintenance:work',
  MAINTENANCE_INVENTORY: 'maintenance:inventory',
  MAINTENANCE_CONFIGURE: 'maintenance:configure',
  // The two halves of the maintenance:view umbrella (see UMBRELLA_HALVES).
  // view_all is the FM's whole register; view_own is a technician's own jobs.
  MAINTENANCE_VIEW_ALL: 'maintenance:view_all',
  MAINTENANCE_VIEW_OWN: 'maintenance:view_own',
  // FM Technician rebuild (spec A.0): issue parts to my own job, raise a fault.
  MAINTENANCE_ISSUE_PARTS: 'maintenance:issue_parts',
  MAINTENANCE_REPORT_FAULT: 'maintenance:report_fault',
  BMS_CONTROL: 'bms:control',

  // Assets
  ASSETS_VIEW: 'asset:view',
  ASSETS_VIEW_ALL: 'asset:view_all',
  ASSETS_CREATE: 'asset:create',
  ASSETS_MANAGE: 'assets:manage',
  ASSETS_DISPOSE: 'assets:dispose',
  ASSETS_IMPORT: 'assets:import',

  // Visitors
  VISITORS_VIEW: 'visitor:view',
  /** Every visit, plus the visitor reports. Without it visitor:view reads only
   *  visits the caller hosts or registered (Employee rebuild A5). */
  VISITORS_VIEW_ALL: 'visitor:view_all',
  VISITORS_PRE_REGISTER: 'visitors:pre_register',
  VISITORS_MANAGE: 'visitors:manage',
  VISITORS_KIOSK: 'visitors:kiosk',
  VISITORS_BLACKLIST: 'visitors:blacklist',
  VISITORS_REPORTS: 'visitors:reports',
  /** Trigger or clear an emergency alert. Backend EMERGENCY_TRIGGER: reception,
   *  fm_manager, org_admin, super_admin. */
  EMERGENCY_TRIGGER: 'emergency:trigger',
  /** Write, schedule and remove announcements (Employee rebuild B21). Backend
   *  ANNOUNCEMENT_MANAGE: fm_manager, plus org_admin / super_admin; the routes
   *  also accept admin:manage_org. */
  ANNOUNCEMENT_MANAGE: 'announcement:manage',

  // Leases
  LEASES_VIEW: 'lease:view',
  LEASES_CREATE: 'lease:create',
  LEASES_MANAGE: 'leases:manage',
  LEASES_FINANCE: 'leases:finance',
  LEASES_IFRS16: 'leases:ifrs16',

  // Contractors
  CONTRACTORS_VIEW: 'contractor:view',
  CONTRACTORS_CREATE: 'contractor:create',
  CONTRACTORS_MANAGE: 'contractors:manage',
  CONTRACTORS_APPROVE_INVOICE: 'contractors:approve_invoice',
  CONTRACTORS_VERIFY: 'contractors:verify',
  // The strings the BACKEND enforces on the vendor/invoice routes (FM Manager
  // R1 A8). APPROVE: approve / reject / suspend / reactivate a vendor and the
  // pending queue. CERTIFY: invoice review and relink. PAY: mark an invoice
  // paid — org_admin only, deliberately not the FM (D3).
  CONTRACTOR_APPROVE: 'contractor:approve',
  CONTRACTOR_CERTIFY: 'contractor:certify',
  CONTRACTOR_PAY: 'contractor:pay',

  // Permits to work (FM Manager R1) and the FM's own contract settings.
  PERMIT_VIEW: 'permit:view',
  PERMIT_ISSUE: 'permit:issue',
  FM_CONTRACT_EDIT: 'fm:contract_edit',
  // The FM Reports page and KPI board (owner decision F4, 28 Oct 2026): every
  // helpdesk:view_all holder except reception, which must not read penalties.
  FM_VIEW_REPORTS: 'fm:view_reports',
  // FM Technician rebuild (spec A.0). VIEW_COSTS: money on FM objects, held by
  // every role except fm_technician. MANAGE_TECHNICIANS: invite technicians,
  // their sites and shifts, the designated-person register, safety officers.
  // SHIFT_LEAD: shift-lead acts, held through the org's "Shift lead" custom role.
  FM_VIEW_COSTS: 'fm:view_costs',
  FM_MANAGE_TECHNICIANS: 'fm:manage_technicians',
  FM_SHIFT_LEAD: 'fm:shift_lead',

  // Tenants
  ASSET_VIEW_OWN: 'asset:view_own',
  BOOKING_CREATE: 'booking:create',
  BOOKING_EDIT: 'booking:edit',
  DIRECTORY_VIEW_EMAIL: 'directory:view_email',
  HYBRID_REQUEST_DESKS: 'hybrid:request_desks',
  HYBRID_SWAP: 'hybrid:swap',
  LEASE_DELETE: 'lease:delete',
  LEASE_EDIT: 'lease:edit',
  PARKING_CREATE: 'parking:create',
  REPORTS_CREATE: 'reports:create',
  REPORTS_EXPORT: 'reports:export',
  REPORTS_VIEW: 'reports:view',
  SPACE_CREATE: 'space:create',
  SPACE_SWAP: 'space:swap',
  TENANT_EDIT: 'tenant:edit',
  VISITOR_CREATE: 'visitor:create',
  WELLBEING_PARTICIPATE: 'wellbeing:participate',
  FINANCE_APPROVE_PAYMENT_RUNS: 'finance:approve_payment_runs',
  FINANCE_APPROVE_WRITE_OFFS: 'finance:approve_write_offs',
  FINANCE_APPROVE_DEPOSIT_REFUNDS: 'finance:approve_deposit_refunds',
  FINANCE_APPROVE_CREDIT_NOTES: 'finance:approve_credit_notes',
  FINANCE_APPROVE_CAM_TRUEUPS: 'finance:approve_cam_trueups',
  TENANT_MANAGE_LOGINS: 'tenant:manage_logins',
  TENANTS_VIEW: 'tenant:view',
  TENANTS_CREATE: 'tenant:create',
  TENANTS_MANAGE: 'tenants:manage',
  TENANTS_BILLING: 'tenants:billing',

  // IoT / shop floor
  // Same strings as the backend (utils/permissions.py) rather than a plural
  // variant, so the two vocabularies cannot drift for this module.
  IOT_VIEW: 'iot:view',
  IOT_MANAGE: 'iot:manage',

  // Parking
  PARKING_VIEW: 'parking:view',
  PARKING_BOOK: 'parking:book',
  PARKING_MANAGE: 'parking:manage',

  // Wellbeing
  WELLBEING_VIEW: 'wellbeing:view',
  WELLBEING_MANAGE: 'wellbeing:manage',
  WELLBEING_SURVEYS: 'wellbeing:surveys',

  // Energy
  ENERGY_VIEW: 'esg:view',
  ENERGY_MANAGE: 'energy:manage',
  ENERGY_READINGS: 'energy:readings',
  ENERGY_CARBON: 'energy:carbon',

  // Compliance
  COMPLIANCE_VIEW: 'compliance:view',
  // Extra C11: the three the API enforces on the compliance writes were not
  // in this map, so a compliance screen fell back to `false` for an editor
  // whenever /auth/me sent no permission list. Granted below exactly as the
  // backend's ROLE_PERMISSIONS grants them (tests/test_permission_map_parity).
  COMPLIANCE_CREATE: 'compliance:create',
  COMPLIANCE_EDIT: 'compliance:edit',
  COMPLIANCE_DELETE: 'compliance:delete',
  COMPLIANCE_MANAGE: 'compliance:manage',
  COMPLIANCE_INSPECTIONS: 'compliance:inspections',
  COMPLIANCE_FIRE_DRILLS: 'compliance:fire_drills',

  // Hybrid
  //
  // SUBMIT_SCHEDULE and APPROVE_SCHEDULE used to read 'hybrid:submit_schedule'
  // and 'hybrid:approve_schedule' — names the backend had never heard of,
  // granted here anyway, marking a feature that was not built. They now name
  // the real permissions, which is exactly what the note in
  // backend/tests/test_permission_map_parity.py said to do on the day it was:
  // "add the real permission to utils/permissions.py, gate the route with it,
  // and delete these two lines." Both lines are out of UI_ONLY.
  //
  // DECIDE, not APPROVE, on the second one: approving and sending back are the
  // same permission, and the row's own approver_id is what decides WHOSE week
  // it reaches. See the note on the backend constant.
  HYBRID_VIEW: 'hybrid:view',
  HYBRID_MANAGE_POLICY: 'hybrid:manage_policy',
  HYBRID_SUBMIT_SCHEDULE: 'hybrid:schedule_submit',
  HYBRID_APPROVE_SCHEDULE: 'hybrid:schedule_decide',

  // Analytics
  ANALYTICS_VIEW: 'analytics:view',
  ANALYTICS_EXPORT: 'analytics:export',

  // Admin
  ADMIN_VIEW: 'admin:view',
  // 🔴 THE REAL ONE. The backend's constant is ADMIN_MANAGE_USERS =
  // "admin:manage_users", and that is what POST /admin/users and
  // POST /admin/users/import require. `ADMIN_USERS` below spells a string the
  // server's catalogue does not contain, so gating anything on it hides that
  // thing from every role including super_admin.
  ADMIN_MANAGE_USERS: 'admin:manage_users',
  // Deep-test EXT-99: the two that gate most of Admin (workflows, forms,
  // templates, sequences, badges, the DSAR queue; the Roles page). They were
  // spelled as raw strings where needed and missing here, so the role-map
  // fallback told an Org Admin they could not manage their own organisation.
  ADMIN_MANAGE_ORG: 'admin:manage_org',
  ADMIN_MANAGE_ROLES: 'admin:manage_roles',
  //: 🔴 NOT A PERMISSION. 'admin:users' is absent from the backend catalogue —
  //: `'admin:users' in ALL_PERMISSIONS` is False. Nothing in the product gates
  //: on it; one test asserts org_admin "has" it, which passes only because the
  //: role's wildcard expansion answers yes to anything. Left in place rather
  //: than removed in a placement fix, and it wants its own look.
  ADMIN_USERS: 'admin:users',
  ADMIN_ROLES: 'admin:roles',
  ADMIN_ORG_SETTINGS: 'admin:org_settings',
  ADMIN_WEBHOOKS: 'admin:webhooks',
  ADMIN_API_KEYS: 'admin:api_keys',
  ADMIN_SSO: 'admin:sso',
  ADMIN_AUDIT_LOGS: 'admin:audit_logs',
  // Held by super_admin alone. The backend has always had it —
  // `_ORG_ADMIN_PERMISSIONS = _ALL_PERMISSIONS - {ADMIN_MANAGE_BILLING}` — but it
  // was missing here, which is why this map used to hand org_admin literally
  // every permission and read as though the two roles were the same thing.
  ADMIN_BILLING: 'admin:manage_billing',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

// ---------------------------------------------------------------------------
// Role -> Permission map
// ---------------------------------------------------------------------------

const ALL_PERMISSIONS = Object.values(PERMISSIONS);

const FM_MANAGER_PERMISSIONS: Permission[] = [
  PERMISSIONS.EMERGENCY_TRIGGER,
  PERMISSIONS.ANNOUNCEMENT_MANAGE,
  // 🔴 NO visitor:*, booking:*, parking:*, space edit/book or hybrid-policy
  // entry (FM Manager Q8, 26 Oct 2026): the role does not see visitors,
  // bookings or parking at all, and the API answers 403.
  PERMISSIONS.HELPDESK_VIEW_ALL,
  PERMISSIONS.FM_VIEW_REPORTS,
  // FM Technician rebuild: the manager keeps money and manages its technicians.
  PERMISSIONS.FM_VIEW_COSTS,
  PERMISSIONS.FM_MANAGE_TECHNICIANS,
  PERMISSIONS.HELPDESK_WORK,
  // The machine register is an asset register by another name, and this role
  // already manages assets and the maintenance that follows from them.
  PERMISSIONS.IOT_VIEW,
  PERMISSIONS.IOT_MANAGE,
  PERMISSIONS.DASHBOARD_VIEW,
  PERMISSIONS.DASHBOARD_ANALYTICS,
  PERMISSIONS.SPACES_VIEW,
  PERMISSIONS.HELPDESK_VIEW,
  PERMISSIONS.HELPDESK_CREATE,
  PERMISSIONS.HELPDESK_MANAGE,
  PERMISSIONS.HELPDESK_ASSIGN,
  PERMISSIONS.HELPDESK_CLOSE,
  PERMISSIONS.HELPDESK_SLA_CONFIG,
  PERMISSIONS.HELPDESK_KB_MANAGE,
  PERMISSIONS.MAINTENANCE_VIEW,
  PERMISSIONS.MAINTENANCE_CREATE_WO,
  PERMISSIONS.MAINTENANCE_MANAGE_WO,
  PERMISSIONS.MAINTENANCE_APPROVE_WO,
  PERMISSIONS.MAINTENANCE_PPM_MANAGE,
  PERMISSIONS.MAINTENANCE_PARTS_MANAGE,
  PERMISSIONS.ASSETS_VIEW,
  PERMISSIONS.ASSETS_CREATE,
  PERMISSIONS.ASSETS_MANAGE,
  PERMISSIONS.ASSETS_DISPOSE,
  PERMISSIONS.ASSETS_IMPORT,
  // 🔴 NO LEASES_* AND NO TENANTS_* (Deep-test EXT-50). They were here while
  // the backend's fm_manager holds neither lease:* nor tenant:* — facility
  // management is not the tenancy, which is property_manager's. So before
  // /auth/me answered, and everywhere hasPerm() reads this map directly, an
  // FM manager was offered the Tenants and Leases surface and every call
  // behind it was a 403. The backend is the source of truth;
  // backend/tests/test_deeptest_ext_050.py fails if this list names a real
  // backend permission the backend's fm_manager does not hold.
  PERMISSIONS.CONTRACTORS_VIEW,
  PERMISSIONS.CONTRACTORS_CREATE,
  PERMISSIONS.CONTRACTORS_MANAGE,
  PERMISSIONS.CONTRACTORS_VERIFY,
  // FM Manager R1: approves/suspends vendors and certifies invoices, but does
  // NOT mark them paid (contractor:pay) — that stays with the Org Admin.
  PERMISSIONS.CONTRACTOR_APPROVE,
  PERMISSIONS.CONTRACTOR_CERTIFY,
  PERMISSIONS.PERMIT_VIEW,
  PERMISSIONS.PERMIT_ISSUE,
  PERMISSIONS.FM_CONTRACT_EDIT,
  PERMISSIONS.WELLBEING_VIEW,
  PERMISSIONS.WELLBEING_MANAGE,
  PERMISSIONS.WELLBEING_SURVEYS,
  PERMISSIONS.ENERGY_VIEW,
  PERMISSIONS.ENERGY_MANAGE,
  PERMISSIONS.ENERGY_READINGS,
  PERMISSIONS.ENERGY_CARBON,
  PERMISSIONS.COMPLIANCE_VIEW,
  // create and edit, NOT delete — the backend's fm_manager set holds
  // compliance:view/create/edit; deactivating a certification is org_admin's.
  PERMISSIONS.COMPLIANCE_CREATE,
  PERMISSIONS.COMPLIANCE_EDIT,
  PERMISSIONS.COMPLIANCE_MANAGE,
  PERMISSIONS.COMPLIANCE_INSPECTIONS,
  PERMISSIONS.COMPLIANCE_FIRE_DRILLS,
  PERMISSIONS.HYBRID_VIEW,
  // 🔴 HYBRID_APPROVE_SCHEDULE WAS HERE, AND ONLY HERE. It was granted to
  // `executive` while nothing of that name existed on the backend, so the
  // grant meant nothing and could not be checked. Now that it names a real
  // permission it has to match the backend, where the executive set is
  // view-only and holds neither schedule permission — deciding somebody's week
  // is not a reporting power. It sits on the employee list below instead,
  // because a team's manager is frequently an employee and the ROW decides
  // whose week they reach.
  PERMISSIONS.ANALYTICS_VIEW,
  PERMISSIONS.ANALYTICS_EXPORT,
];

/** Everything except the one permission reserved for the platform operator.
 *
 *  Mirrors the backend exactly (`utils/permissions.py`), where org_admin is
 *  `_ALL_PERMISSIONS - {ADMIN_MANAGE_BILLING}`.
 */
const ORG_ADMIN_PERMISSIONS: Permission[] = ALL_PERMISSIONS.filter(
  (p) => p !== PERMISSIONS.ADMIN_BILLING,
);

/** Everything a role can only look at, plus the two exports.
 *
 *  Mirrors the backend's `_EXECUTIVE_PERMISSIONS = _VIEW_ONLY | {ANALYTICS_EXPORT,
 *  REPORTS_EXPORT}`. There is no reports:* permission on this side, so the
 *  analytics export stands in for both.
 */
// 🔴 DERIVED, MINUS TWO. This used to be every `:view` permission, which is
// what made it drift from the backend the moment the backend narrowed: the
// sidebar would keep rendering a Visitors entry for a role the API now
// refuses, and the page behind it would 403. A control offered to someone who
// cannot complete it is the failure this repo has a guard for.
//
// visitor:view      visitor names, emails and phones are THIRD-PARTY personal
//                   data. The dashboard's visitor tile is unaffected — it
//                   counts by visitor type and names nobody.
// directory:view_email  the staff email list. Names stay; addresses go.
//
// See _EXECUTIVE_PERMISSIONS in backend/utils/permissions.py for why the
// surface is REMOVED rather than masked.
const EXECUTIVE_WITHHELD: string[] = [
  PERMISSIONS.VISITORS_VIEW,
];

const EXECUTIVE_PERMISSIONS: Permission[] = [
  ...ALL_PERMISSIONS.filter(
    (p) => p.endsWith(':view') && !EXECUTIVE_WITHHELD.includes(p),
  ),
  // Do not end in ':view', so named: the executive read every ticket and every
  // booking before the employee rebuild and still does. Not visitor:view_all.
  PERMISSIONS.HELPDESK_VIEW_ALL,
  PERMISSIONS.BOOKING_VIEW_ALL,
  PERMISSIONS.DASHBOARD_ANALYTICS,
  PERMISSIONS.ANALYTICS_EXPORT,
  // The FM reports page and KPI board, read-only (owner decision F4).
  PERMISSIONS.FM_VIEW_REPORTS,
];

/**
 * The nine roles the backend can actually issue.
 *
 * This map used to name eighteen, of which thirteen — facility_manager,
 * maintenance_manager, technician, front_desk, security, finance, hr,
 * department_head, tenant_user, vendor, visitor, kiosk and api_service — were
 * not values of UserRoleEnum and could never arrive on a user. Four that can
 * (fm_manager, fm_technician, reception, executive) were absent, and that was
 * not cosmetic: `hasPerm` looks the role up here and returns false when the key
 * is missing, while every one of the sidebar's nineteen items is gated on a
 * permission. An fm_manager, fm_technician, reception or executive user
 * therefore signed in to an empty sidebar.
 *
 * Keys are UserRoleEnum in models/auth.py. Keep them in step.
 */
export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  super_admin: ALL_PERMISSIONS,
  org_admin: ORG_ADMIN_PERMISSIONS,
  fm_manager: FM_MANAGER_PERMISSIONS,
  property_manager: [
    // Property Manager rebuild A9: exactly the backend's set (_PROPERTY_MANAGER_PERMISSIONS), and no
    // frontend-only names; tests/test_permission_map_parity.py holds them equal. P3 took visitor:view_all,
    // booking:view_all, visitor:export_profile and space:edit. Approving money isn't here (P4).
    PERMISSIONS.ANALYTICS_EXPORT,
    PERMISSIONS.ANALYTICS_VIEW,
    PERMISSIONS.ASSETS_VIEW_ALL,
    PERMISSIONS.ASSET_VIEW_OWN,
    PERMISSIONS.BOOKING_CREATE,
    PERMISSIONS.BOOKING_EDIT,
    PERMISSIONS.BOOKING_VIEW,
    PERMISSIONS.DIRECTORY_VIEW_EMAIL,
    PERMISSIONS.FM_VIEW_COSTS,
    PERMISSIONS.FM_VIEW_REPORTS,
    PERMISSIONS.HELPDESK_CREATE,
    PERMISSIONS.HELPDESK_VIEW,
    PERMISSIONS.HELPDESK_VIEW_ALL,
    PERMISSIONS.HYBRID_REQUEST_DESKS,
    PERMISSIONS.HYBRID_APPROVE_SCHEDULE,
    PERMISSIONS.HYBRID_SUBMIT_SCHEDULE,
    PERMISSIONS.HYBRID_SWAP,
    PERMISSIONS.HYBRID_VIEW,
    PERMISSIONS.LEASES_CREATE,
    PERMISSIONS.LEASE_DELETE,
    PERMISSIONS.LEASE_EDIT,
    PERMISSIONS.LEASES_VIEW,
    PERMISSIONS.PARKING_CREATE,
    PERMISSIONS.PARKING_VIEW,
    PERMISSIONS.REPORTS_CREATE,
    PERMISSIONS.REPORTS_EXPORT,
    PERMISSIONS.REPORTS_VIEW,
    PERMISSIONS.SPACE_CREATE,
    PERMISSIONS.SPACE_SWAP,
    PERMISSIONS.SPACES_VIEW,
    PERMISSIONS.TENANT_EDIT,
    PERMISSIONS.TENANTS_CREATE,
    PERMISSIONS.TENANT_MANAGE_LOGINS,
    PERMISSIONS.TENANTS_VIEW,
    PERMISSIONS.VISITOR_CREATE,
    PERMISSIONS.VISITORS_VIEW,
    PERMISSIONS.WELLBEING_PARTICIPATE,
    PERMISSIONS.WELLBEING_VIEW,
  ],
  fm_technician: [
    // 🔴 REBUILT FROM THE BACKEND (FM Technician rebuild R1, 29 Oct 2026). Only
    // strings the backend's fm_technician set holds — no frontend-only names —
    // so this fallback says exactly what the server enforces. Gone with A.0/D5:
    // helpdesk:view_all, visitor:view_all, booking:view_all, fm:view_reports,
    // maintenance:inventory, fm:view_costs. The technician reads its OWN jobs
    // (maintenance:view_own) and works them (maintenance:work); tickets through
    // helpdesk:work. Normal employee self-service stays.
    PERMISSIONS.BOOKING_VIEW,
    PERMISSIONS.IOT_VIEW,
    PERMISSIONS.PERMIT_VIEW,
    PERMISSIONS.HELPDESK_VIEW,
    PERMISSIONS.HELPDESK_CREATE,
    PERMISSIONS.HELPDESK_WORK,
    PERMISSIONS.MAINTENANCE_VIEW_OWN,
    PERMISSIONS.MAINTENANCE_WORK,
    PERMISSIONS.MAINTENANCE_ISSUE_PARTS,
    PERMISSIONS.MAINTENANCE_REPORT_FAULT,
    PERMISSIONS.ASSETS_VIEW_ALL,
    PERMISSIONS.SPACES_VIEW,
    PERMISSIONS.VISITORS_VIEW,
    PERMISSIONS.PARKING_VIEW,
    PERMISSIONS.WELLBEING_VIEW,
    PERMISSIONS.HYBRID_VIEW,
    PERMISSIONS.HYBRID_SUBMIT_SCHEDULE,
    // Held broadly on purpose, and inert for almost everyone: what narrows it
    // is the schedule's own stamped approver_id, not the permission. Mirrors
    // the backend's employee set.
    PERMISSIONS.HYBRID_APPROVE_SCHEDULE,
  ],
  reception: [
    PERMISSIONS.BOOKING_VIEW,
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.EMERGENCY_TRIGGER,
    PERMISSIONS.VISITORS_VIEW,
    PERMISSIONS.VISITORS_PRE_REGISTER,
    PERMISSIONS.VISITORS_MANAGE,
    PERMISSIONS.VISITORS_KIOSK,
    PERMISSIONS.VISITORS_VIEW_ALL,
    // Reception rebuild R3: own tickets only (no helpdesk:view_all).
    PERMISSIONS.HELPDESK_VIEW,
    PERMISSIONS.HELPDESK_CREATE,
    PERMISSIONS.BOOKING_VIEW_ALL,
    PERMISSIONS.CATERING_ORDER_STATUS,
    PERMISSIONS.PARKING_VIEW,
    PERMISSIONS.PARKING_MANAGE,
    PERMISSIONS.PARKING_BOOK,
    PERMISSIONS.SPACES_VIEW,
    PERMISSIONS.SPACES_BOOK_DESK,
    PERMISSIONS.SPACES_BOOK_ROOM,
    PERMISSIONS.SPACES_MANAGE_BOOKINGS,
    PERMISSIONS.WELLBEING_VIEW,
    PERMISSIONS.HYBRID_VIEW,
  ],
  executive: EXECUTIVE_PERMISSIONS,
  // The narrowest role in the product. docs/18 lists six responsibilities and
  // only two exist — DSAR processing and right-to-erasure — so this grants a
  // dashboard to land on and nothing else. The privacy:* permissions the
  // backend checks are not in this map because no CLIENT control is gated on
  // them yet; when a privacy screen exists, add them here and in PERMISSIONS.
  dpo: [
    PERMISSIONS.DASHBOARD_VIEW,
  ],
  employee: [
    PERMISSIONS.BOOKING_VIEW,
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.SPACES_VIEW,
    PERMISSIONS.SPACES_BOOK_DESK,
    PERMISSIONS.SPACES_BOOK_ROOM,
    PERMISSIONS.HELPDESK_VIEW,
    PERMISSIONS.HELPDESK_CREATE,
    PERMISSIONS.VISITORS_VIEW,
    PERMISSIONS.VISITORS_PRE_REGISTER,
    PERMISSIONS.PARKING_VIEW,
    PERMISSIONS.PARKING_BOOK,
    PERMISSIONS.WELLBEING_VIEW,
    PERMISSIONS.HYBRID_VIEW,
    PERMISSIONS.HYBRID_SUBMIT_SCHEDULE,
    // Held broadly on purpose, and inert for almost everyone: what narrows it
    // is the schedule's own stamped approver_id, not the permission. Managing
    // a team is not a role here — team_schedules.manager_id frequently names
    // somebody whose role is `employee`. Mirrors the backend's employee set.
    PERMISSIONS.HYBRID_APPROVE_SCHEDULE,
  ],
  // T1: the dormant role holds nothing; real tenant admins use the portal.
  tenant_admin: [],
  // C17: the dormant role holds nothing; real contractors use the portal.
  contractor: [],
};

// ---------------------------------------------------------------------------
// Permission check helpers
// ---------------------------------------------------------------------------

/**
 * Is this the platform operator, rather than a customer's own administrator?
 *
 * The permission map cannot express what actually separates the two roles. The
 * backend gates eleven things on `user.role != SUPER_ADMIN` directly, not on a
 * permission, so none of them appears in the list `/auth/me` returns:
 *
 *   GET/POST/PATCH/DELETE  /admin/organisations[/{id}]   list, create, rename, delete
 *   POST                   /admin/organisations/purge    hard-delete
 *   GET                    /admin/scheduler              deployment job state
 *   POST/DELETE            /compliance/public-holidays   when deployment_wide
 *   POST/PUT               /admin/users[/{id}], bulk     assigning super_admin
 *
 * Every one of those crosses organisation boundaries, which is the whole
 * distinction: super_admin is not a senior tenant role, it administers the
 * deployment. `org_admin` holds everything else, including renaming its own
 * organisation and changing every setting.
 *
 * So the role check stays a role check. This function exists to give it one
 * name, and to keep that reasoning next to the map rather than inline in a
 * sidebar filter.
 */
export function isPlatformOperator(
  user: { role: UserRole } | null | undefined,
): boolean {
  return user?.role === 'super_admin';
}

/**
 * Roles that name a portal nobody can sign in to yet.
 *
 * `contractor` and `tenant_admin` each mean "this principal sees ONE contractor
 * / ONE tenant", and the row that would say WHICH one is created by nothing in
 * either codebase. So an account holding either could not be narrowed at all
 * and read the whole organisation instead — every contractor, credential and
 * invoice in it. The backend now refuses the sign-in outright and refuses to
 * assign the role (routers/auth.py, routers/admin.py; PORTAL_ROLE_SCOPES in
 * backend/utils/permissions.py is the same list).
 *
 * They stay in ROLE_PERMISSIONS above and in each page's full role list,
 * because an account somewhere already holds one and an administrator has to
 * be able to FILTER the user list to find it. What they come out of is the
 * ASSIGN picker — which is the difference this function draws.
 *
 * Delete an entry here when its portal genuinely exists, and in the same change
 * as the backend's, never before it.
 *
 * Since Bug_78/82 the pickers no longer filter a literal list with this: they
 * read GET /admin/users/role-options (hooks/useRoleOptions), whose
 * `staff_assignable: false` marks these same two roles. Invite User OFFERS them
 * — and creates a PORTAL account for the chosen company, never a staff row —
 * while Edit and the bulk role change, which act on staff rows, do not.
 */
export const UNSCOPED_PORTAL_ROLES: readonly string[] = ['contractor', 'tenant_admin'];

/**
 * The subset of a role picker's options the current user may actually assign.
 *
 * Offering "Super Admin" to someone who cannot grant it is not a security hole —
 * the backend refuses with 403 either way — but it is an invitation to try, and
 * a control that fails on submit teaches people the app is unreliable. The same
 * argument removes the two unscoped portal roles, for everyone including the
 * platform operator: those are not a matter of rank, the account is broken
 * whoever creates it.
 */
export function assignableRoleOptions<T extends { value: string }>(
  options: readonly T[],
  user: { role: UserRole } | null | undefined,
): T[] {
  const assignable = options.filter((o) => !UNSCOPED_PORTAL_ROLES.includes(o.value));
  if (isPlatformOperator(user)) return assignable;
  return assignable.filter((o) => o.value !== 'super_admin');
}

export function hasPerm(userRole: UserRole, permission: Permission): boolean {
  const rolePerms = ROLE_PERMISSIONS[userRole];
  if (!rolePerms) return false;
  return rolePerms.includes(permission);
}

/**
 * Permission check that prefers the user's effective permission list (from
 * /auth/me — includes custom-role JSONB perms) over the static role map.
 * Falls back to the role-based check when the list isn't populated.
 *
 * Pass `null` user when not yet hydrated → returns false (deny by default).
 */
/**
 * 🔴 TWO UMBRELLAS THE SERVER NO LONGER HANDS OUT. `maintenance:view` and
 * `asset:view` were split into `_all` / `_own`, and a394c2e moved sixty-five
 * backend routes off the umbrellas — but the sidebar still asked for them, and
 * matched the /auth/me list exactly. Only org_admin and super_admin still hold
 * the umbrella strings, so every fm_manager, fm_technician and executive had
 * no Maintenance, Assets, Scan Asset or Checklists link at all, while the
 * pages themselves loaded for them. Caught in the browser as fm_manager on
 * 29 Sep. Either half satisfies the umbrella, as it does on the server.
 */
export const UMBRELLA_HALVES: Record<string, string[]> = {
  'maintenance:view': ['maintenance:view_all', 'maintenance:view_own'],
  'asset:view': ['asset:view_all', 'asset:view_own'],
};

/**
 * 🔴 THE STAFF PERMISSIONS IMPLY THEIR view_all HALF (Employee rebuild A,
 * review R1C) — the server's _EXPANDS entries (utils/permissions.py
 * VIEW_ALL_SOURCES), mirrored so a list that arrives unexpanded reads the same.
 * /auth/me already sends the expanded set; this keeps the two from disagreeing
 * if any other path hands the client a raw role list.
 * tests/test_employee_role_matrix.py pins this block to the server's.
 */
export const VIEW_ALL_SOURCES: Record<string, string[]> = {
  'helpdesk:view_all': ['helpdesk:assign', 'helpdesk:delete', 'helpdesk:edit'],
  'visitor:view_all': ['visitor:approve', 'visitor:checkin', 'visitor:delete', 'visitor:edit'],
  'booking:view_all': ['booking:approve', 'booking:create_for_others', 'booking:delete'],
};

export function hasPermFor(
  user: { role: UserRole; permissions?: string[] | null } | null | undefined,
  permission: string,
): boolean {
  if (!user) return false;
  if (Array.isArray(user.permissions)) {
    const held = user.permissions;
    return held.includes(permission)
      || (UMBRELLA_HALVES[permission] ?? []).some((half) => held.includes(half))
      || (VIEW_ALL_SOURCES[permission] ?? []).some((source) => held.includes(source));
  }
  return hasPerm(user.role, permission as Permission);
}

export function hasAnyPerm(userRole: UserRole, permissions: Permission[]): boolean {
  return permissions.some((p) => hasPerm(userRole, p));
}

export function hasAllPerms(userRole: UserRole, permissions: Permission[]): boolean {
  return permissions.every((p) => hasPerm(userRole, p));
}

// ---------------------------------------------------------------------------
// PermissionGate component
// ---------------------------------------------------------------------------

interface PermissionGateProps {
  children: React.ReactNode;
  permission?: Permission;
  permissions?: Permission[];
  requireAll?: boolean;
  userRole: UserRole;
  fallback?: React.ReactNode;
}

export function PermissionGate({
  children,
  permission,
  permissions,
  requireAll = false,
  userRole,
  fallback = null,
}: PermissionGateProps): React.ReactNode {
  let hasAccess = false;

  if (permission) {
    hasAccess = hasPerm(userRole, permission);
  } else if (permissions) {
    hasAccess = requireAll
      ? hasAllPerms(userRole, permissions)
      : hasAnyPerm(userRole, permissions);
  } else {
    hasAccess = true;
  }

  return hasAccess ? children : fallback;
}
